import { useCallback, useEffect, useMemo, useState } from 'react';
import { assignTheme, createTheme, deleteTheme, getThemes } from '../../data/themes';

// Themes and assignments for whichever page shows a positions table.
//
// Loaded separately from the holdings, not folded into them: the holdings are a
// cached snapshot rebuilt on a schedule, and an assignment has to show the
// moment it is made, not at the next rebuild.
//
// Changes are applied locally first and rolled back if the server refuses, so
// picking a theme from a row's dropdown does not wait on a round trip.

/** The same normalisation the server applies, so a lookup hits either way. */
export const themeKey = (symbol) => (symbol ? String(symbol).trim().toUpperCase() : '');

/**
 * A catalogue key for a failed theme request, never the server's own text.
 *
 * The API answers in English whatever the reader's language, so showing
 * `err.message` would put an English sentence in the middle of a Korean page.
 * The status is all the UI needs to say what went wrong.
 */
const errorKey = (err, fallback) => {
    if (err?.status === 401) return 'themes.errors.session';
    if (err?.status === 0) return 'themes.errors.offline';
    if (err?.status === 404) return 'themes.errors.gone';
    if (err?.status === 409) return 'themes.errors.duplicate';
    return fallback;
};

export default function useThemes() {
    const [themes, setThemes] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => {
        // StrictMode double-invokes effects in development; see Holdings.
        let cancelled = false;
        getThemes()
            .then((data) => {
                if (cancelled) return;
                setThemes(data.themes ?? []);
                setAssignments(data.assignments ?? {});
            })
            // Themes are an overlay on the table, not the table. Failing to load
            // them must not take the holdings down with them.
            .catch((err) => { if (!cancelled) setError(errorKey(err, 'themes.errors.load')); });
        return () => { cancelled = true; };
    }, []);

    const byId = useMemo(() => new Map(themes.map((th) => [th.id, th])), [themes]);

    /** The asset's theme, or null for "Not assigned". */
    const themeOf = useCallback(
        (symbol) => byId.get(assignments[themeKey(symbol)]) ?? null,
        [byId, assignments],
    );

    const counts = useMemo(() => {
        const c = new Map();
        for (const id of Object.values(assignments)) c.set(id, (c.get(id) ?? 0) + 1);
        return c;
    }, [assignments]);

    const assign = useCallback(async (symbol, themeId) => {
        const key = themeKey(symbol);
        const previous = assignments[key] ?? null;
        const apply = (id) => setAssignments((current) => {
            const next = { ...current };
            if (id) next[key] = id; else delete next[key];
            return next;
        });
        apply(themeId);
        setError(null);
        try {
            await assignTheme(key, themeId);
        } catch (err) {
            apply(previous);
            setError(errorKey(err, 'themes.errors.save'));
        }
    }, [assignments]);

    const create = useCallback(async (name) => {
        setError(null);
        try {
            const theme = await createTheme(name);
            setThemes((current) =>
                [...current, theme].sort((a, b) => a.name.localeCompare(b.name)));
            return true;
        } catch (err) {
            setError(errorKey(err, 'themes.errors.save'));
            return false;
        }
    }, []);

    const remove = useCallback(async (id) => {
        setError(null);
        // Mirror the server's cascade rather than refetching.
        const drop = () => {
            setThemes((current) => current.filter((th) => th.id !== id));
            setAssignments((current) =>
                Object.fromEntries(Object.entries(current).filter(([, v]) => v !== id)));
        };
        try {
            await deleteTheme(id);
            drop();
        } catch (err) {
            // Someone else deleted it first: the outcome asked for is the one
            // that exists, so it is not an error worth showing.
            if (err?.status === 404) drop();
            else setError(errorKey(err, 'themes.errors.delete'));
        }
    }, []);

    /** `error` is a catalogue key — pass it through `t`. */
    return { themes, themeOf, counts, assign, create, remove, error };
}

/**
 * Sort helper for a table's Theme column: by theme name, "Not assigned" last
 * whichever direction is active, and by value (largest first) within a theme so
 * each group reads biggest holding first.
 */
export function compareByTheme(themeOf, direction, valueKey) {
    return (a, b) => {
        const an = themeOf(a.symbol)?.name ?? null;
        const bn = themeOf(b.symbol)?.name ?? null;
        if (an !== bn) {
            if (an === null) return 1;
            if (bn === null) return -1;
            const cmp = an.localeCompare(bn);
            return direction === 'asc' ? cmp : -cmp;
        }
        return Number(b[valueKey] || 0) - Number(a[valueKey] || 0);
    };
}
